/**
 * Seller em análise ou pausado (pay #619): o `/applications/params` passa a dizer se a plataforma
 * pode receber agora, e a cobrança pode voltar 409 `seller_not_active` quando o status muda com a
 * página já aberta. Os dois são estado de negócio esperado, não erro do servidor.
 */

/**
 * O checkout trava só com `paymentsAvailable === false`. Ausente (pay antigo, params que não
 * carregou) é "disponível": o pay sobe primeiro só observando, e isto não pode mudar nada até ele
 * mandar `false`. Grátis não cobra, e o preview do builder é o admin desenhando a página.
 */
export function arePaymentsUnavailable(
	finance: { paymentsAvailable?: boolean } | null | undefined,
	options: { isFree?: boolean; isPreview?: boolean } = {}
): boolean {
	if (options.isPreview || options.isFree) return false;
	return finance?.paymentsAvailable === false;
}

/** 409 `{ code: 'seller_not_active' }` da cobrança. */
export function isSellerNotActiveError(error: unknown): boolean {
	const response = (
		error as {
			response?: { status?: number; data?: { code?: unknown } } | null;
		}
	)?.response;
	return (
		response?.status === 409 && response?.data?.code === 'seller_not_active'
	);
}

/**
 * O rótulo de preço da tabela de planos cobra? É texto livre do builder ("R$ 97", "Grátis"), então
 * a regra é a mais simples que não erra para o lado ruim: cobra se tiver algum dígito diferente de
 * zero. "Grátis", "R$ 0,00" e vazio não cobram. Um rótulo pago lido como grátis só deixa o CTA
 * ativo — e o checkout do plano, que tem o preço de verdade, trava do mesmo jeito.
 */
export function isPaidPriceLabel(label: unknown): boolean {
	return /[1-9]/.test(
		typeof label === 'string' ? label : String(label ?? '')
	);
}

type PricingBlock = {
	type: string;
	props?: Record<string, any>;
};

/** Os preços que a tabela de planos oferece: o de cada coluna e, com o alternador, o anual. */
function pricingLabels(blocks: PricingBlock[] = []): unknown[] {
	return blocks.flatMap((block) => {
		if (block.type === 'columns') {
			const children: PricingBlock[][] = block.props?.children ?? [];
			return children.flatMap((column) => pricingLabels(column));
		}
		if (block.type !== 'pricingTable') return [];
		const columns: { price?: string; priceYearly?: string }[] =
			block.props?.columns ?? [];
		return columns.flatMap((column) =>
			block.props?.billingToggle
				? [column.price, column.priceYearly || column.price]
				: [column.price]
		);
	});
}

/**
 * Tela de planos com a plataforma sem poder receber: o que travar. A regra é a mesma do checkout
 * (só trava o que cobra), levada aos planos da página:
 * - `none`: nenhum plano cobra (ou não há tabela de planos) — a página segue normal;
 * - `all`: todos cobram — nada a fazer aqui, a página inteira vira o estado indisponível;
 * - `some`: há grátis e pagos — a página segue, e só os CTAs dos pagos ficam indisponíveis.
 */
export function pricingPageChargeScope(
	page: { blocks?: PricingBlock[] } | null | undefined
): 'none' | 'some' | 'all' {
	const paid = pricingLabels(page?.blocks).map(isPaidPriceLabel);
	if (!paid.some(Boolean)) return 'none';
	return paid.every(Boolean) ? 'all' : 'some';
}
